import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowRightIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { BoltIcon, ChartBarIcon, BanknotesIcon } from '@heroicons/react/24/solid'
import GrafikaOferta from '../home/GrafikaOferta'
import IkonaAsystenta from '../ikony/IkonaAsystenta'
import GrafikaProjekt from '../oferta/GrafikaProjekt'
import { linkDoSprawdzeniaOferty } from '../../lib/linkOfert'
import { zdarzenie } from '../../lib/pomiar'
import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'
import { jestPolska } from '../../kraj'

/**
 * Kafelki zachęty na stronie modelu (prośba właściciela 2026-10-08).
 *
 * PO CO: większość wejść z Google i ChatGPT ląduje prosto na karcie konkretnego produktu. Stamtąd
 * zapraszamy dalej: z lewej „Oferta pod lupą” (ktoś z ofertą na ten sprzęt), z prawej „Asystent doboru”
 * (ktoś, kto dopiero składa zestaw). Wygląd jak kafelek „Oferta pod lupą” na stronie głównej i kafelek
 * VoltValue Projekt: biała karta, rysunek, niebieska fala z białym przyciskiem.
 *
 * Na szerokim ekranie (od 1840 px, gdy obok treści 80rem mieszczą się dwa kafelki po 240 px) kafelki stoją
 * w pustych marginesach i jadą z przewijaniem; węższe ekrany dostają je obok siebie pod listą ofert.
 * Klik liczy zdarzenie `zacheta` (cel „oferta:panel”, „asystent:inwerter”…), żeby zmierzyć, czy działają.
 *
 * Teksty w słowniku `model` (zachety.*). Wersja krajowa: bez „Oferta pod lupą” (narzędzie pod polskie
 * dotacje i VAT) i bez plakietki „Zwrot” (opłacalność liczymy tylko dla Polski); zostaje sam asystent.
 */

// Kategorie z kafelkami; „z tym modułem” itd. w słowniku (zachety.z_tym.<kategoria>).
export const KATEGORIE_Z_ZACHETA = ['panel', 'inwerter', 'magazyn']
const useT = () => useTranslation('model', { i18n }).t

/** Niebieska fala z białym przyciskiem (kolory na sztywno: w ciemnym motywie zostają takie same). */
const Fala = ({ Ikona, napis }) => (
  <div className="relative -mt-9">
    <svg viewBox="0 0 320 56" preserveAspectRatio="none" aria-hidden="true" className="block h-11 w-full">
      <path d="M0 22C70 4 150 40 230 18C270 8 300 10 320 16V56H0Z" fill="#3b82f6" fillOpacity="0.28" />
      <path d="M0 38C80 18 170 54 250 30C285 20 305 22 320 26V56H0Z" fill="#2563eb" />
    </svg>
    <div className="bg-gradient-to-b from-[#2563eb] to-[#1d4ed8] px-4 pb-4 pt-0.5">
      <span className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#ffffff] px-3 py-2.5 text-sm font-semibold
                       text-[#1d4ed8] shadow-sm transition-colors group-hover:bg-[#eff6ff]">
        <Ikona className="h-5 w-5 flex-shrink-0" />
        {napis}
        <ArrowRightIcon className="h-4 w-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
      </span>
    </div>
  </div>
)

const KLASA_KAFELKA = `vv-kafel group relative flex flex-col overflow-hidden print:hidden
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`

export const KafelekOferty = ({ kategoria, modelId, className = '' }) => {
  const t = useT()
  return (
  <Link to={linkDoSprawdzeniaOferty(kategoria, modelId)} onClick={() => zdarzenie('zacheta', `oferta:${kategoria}`)}
        className={`${KLASA_KAFELKA} ${className}`}>
    <div className="relative px-4 pt-4">
      <p className="text-sm font-semibold text-blue-600">{t('zachety.oferta_naglowek')}</p>
      <h2 className="mt-0.5 text-base font-bold leading-snug text-gray-900">{t('zachety.oferta_tytul', { zTym: t(`zachety.z_tym.${kategoria}`) })}</h2>
      <p className="mt-1 text-[13px] leading-5 text-gray-600">{t('zachety.oferta_opis')}</p>
    </div>
    <div className="relative z-10 flex flex-1 items-center justify-center px-3 pt-2">
      <GrafikaOferta className="w-full max-w-[190px] -rotate-2 drop-shadow-[0_12px_18px_rgba(15,23,42,0.2)] transition-transform
                                duration-300 group-hover:rotate-0" />
    </div>
    <Fala Ikona={DocumentArrowUpIcon} napis={t('zachety.oferta_przycisk')} />
  </Link>
  )
}

/**
 * Asystent z tym produktem przypiętym do zestawu (`przypnij=inwerter-123`, KitAssistant): silnik buduje
 * zestaw wokół tego modelu (`pinned_model_id`). Bez modelu (oferta bez przypisania) zwykłe wejście.
 */
export const linkDoAsystenta = (kategoria, modelId) => (KATEGORIE_Z_ZACHETA.includes(kategoria) && Number(modelId) > 0
  ? `/dobor-zestawu-pv?czego=panels,inverter,battery&przypnij=${kategoria}-${Number(modelId)}`
  : '/dobor-zestawu-pv')

export const KafelekAsystenta = ({ kategoria, modelId, className = '' }) => {
  const t = useT()
  return (
  <Link to={linkDoAsystenta(kategoria, modelId)} onClick={() => zdarzenie('zacheta', `asystent:${kategoria}`)}
        className={`${KLASA_KAFELKA} ${className}`}>
    <div className="relative px-4 pt-4">
      <p className="text-sm font-semibold text-blue-600">{t('zachety.asystent_naglowek')}</p>
      <h2 className="mt-0.5 text-base font-bold leading-snug text-gray-900">
        {Number(modelId) > 0
          ? t('zachety.asystent_z_modelem', { zTym: t(`zachety.z_tym.${kategoria}`) })
          : t('zachety.asystent_bez_modelu')}
      </h2>
      <div className="mt-2 flex flex-wrap gap-1">
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-semibold text-white">
          <BoltIcon className="h-3 w-3" /> {t('zachety.schemat')}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
          <ChartBarIcon className="h-3 w-3" /> {t('zachety.produkcja')}
        </span>
        {jestPolska && (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
            <BanknotesIcon className="h-3 w-3" /> {t('zachety.zwrot')}
          </span>
        )}
      </div>
    </div>
    <div className="relative z-10 flex flex-1 items-center justify-center px-3 pt-3">
      <GrafikaProjekt className="w-full max-w-[200px] -rotate-2 drop-shadow-[0_12px_18px_rgba(15,23,42,0.2)] transition-transform
                                 duration-300 group-hover:rotate-0" />
    </div>
    <Fala Ikona={IkonaAsystenta} napis={t('zachety.asystent_przycisk')} />
  </Link>
  )
}

/**
 * Dwa kafelki w marginesach strony modelu (tylko bardzo szeroki ekran). Kolumny `absolute` na wysokość
 * strony, kafelek w środku `sticky`, więc jedzie z przewijaniem i kończy się nad stopką.
 * Rodzic musi mieć `relative`. Pozycja liczona od środka strony: 40rem (połowa treści) + 1.5rem odstępu.
 */
export const ZachetyWMarginesach = ({ kategoria, modelId }) => {
  const t = useT()
  if (!KATEGORIE_Z_ZACHETA.includes(kategoria)) return null
  return (
    <div aria-label={t('zachety.co_dalej')} role="complementary" className="pointer-events-none absolute inset-0 hidden min-[1840px]:block print:hidden">
      {jestPolska && (
        <div className="absolute inset-y-0 right-[calc(50%_+_40rem_+_1.5rem)] w-60">
          <div className="pointer-events-auto sticky top-24"><KafelekOferty kategoria={kategoria} modelId={modelId} /></div>
        </div>
      )}
      <div className="absolute inset-y-0 left-[calc(50%_+_40rem_+_1.5rem)] w-60">
        <div className="pointer-events-auto sticky top-24"><KafelekAsystenta kategoria={kategoria} modelId={modelId} /></div>
      </div>
    </div>
  )
}

/** Te same kafelki obok siebie w treści strony, gdy marginesy są za wąskie. */
export const ZachetyWTresci = ({ kategoria, modelId, className = '' }) => {
  if (!KATEGORIE_Z_ZACHETA.includes(kategoria)) return null
  return (
    <div className={`grid gap-4 ${jestPolska ? 'sm:grid-cols-2' : 'max-w-sm'} min-[1840px]:hidden print:hidden ${className}`}>
      {jestPolska && <KafelekOferty kategoria={kategoria} modelId={modelId} />}
      <KafelekAsystenta kategoria={kategoria} modelId={modelId} />
    </div>
  )
}
